/**
 * One unit result, as board 03 draws it.
 *
 * The crop on the left is the point of the whole screen. A database saying a
 * story exists is a claim; a picture of the column it was printed in is
 * evidence, and the fraction printed underneath says how much of the page that
 * crop is. A guest gets a placeholder that names what belongs there and why it
 * is absent, never a broken image and never something that mimics loading.
 */

import { Link } from 'react-router-dom'
import type { SearchUnitResult } from '../../lib/api'
import { regionUrl } from '../../lib/api'
import { CropPlaceholder, LOW_CONFIDENCE, TypeBadge } from '../Page'
import { cropMeasure, n } from './model'

export interface ResultCardProps {
  unit: SearchUnitResult
  /**
   * The page's raster size as the enclosing page group reported it. The unit
   * now carries its own, so this is only a fallback for a group whose units
   * were built by the compatibility shim.
   */
  widthPx?: number | null
  heightPx?: number | null
  query: string
  onClip?: (unit: SearchUnitResult) => void
  clipped?: boolean
  /** Page rows draw their units flush with the row's own left inset. */
  dense?: boolean
}

export function ResultCard({
  unit,
  widthPx,
  heightPx,
  query,
  onClip,
  clipped,
  dense = false,
}: ResultCardProps) {
  const low = unit.confidence !== null && unit.confidence < LOW_CONFIDENCE
  // The unit's own page size first: it is what lets the flat Articles view
  // print the crop as a fraction of the page rather than in raw pixels.
  const measure = cropMeasure(
    unit.region,
    unit.width_px ?? widthPx,
    unit.height_px ?? heightPx,
  )
  const openHref = unit.page_id
    ? `/page/${unit.page_id}?${new URLSearchParams({ q: query, unit: unit.id }).toString()}`
    : null

  return (
    <article
      // Stacked on a phone, side by side from the small breakpoint up: at 390
      // the crop and the text together leave the snippet about twenty
      // characters a line, which is not reading, it is a column of syllables.
      className={`flex flex-col gap-3 border-b border-n300 sm:flex-row sm:gap-[15px] ${
        dense ? 'px-4 py-4 sm:px-[18px]' : 'px-4 py-5 sm:px-[22px]'
      }`}
    >
      <div className="flex w-[146px] flex-none flex-col gap-[5px]">
        {unit.restricted || !unit.page_id || !unit.region ? (
          <CropPlaceholder
            className="h-[104px] w-[146px]"
            lines={unit.restricted ? ['PAGE CROP', 'SIGN IN'] : ['NO REGION', 'RECORDED']}
          />
        ) : (
          <Link
            to={openHref ?? '#'}
            className="flex h-full max-h-[220px] items-start justify-center overflow-hidden rounded-[1px] bg-well"
            aria-label={`Open ${unit.headline ?? 'this result'} on the page it was printed on`}
          >
            {/*
              A tall single-column region at 146px wide would run to six hundred
              pixels and leave the row mostly empty. The crop is scaled to fit
              rather than cut off: the whole region stays visible, letterboxed
              on the well, and the fraction printed underneath still states its
              true size. Trimming it instead would show part of a region while
              labelling it as the whole.
            */}
            <img
              src={unit.crop_url ?? regionUrl(unit.page_id, unit.region, 292)}
              alt=""
              loading="lazy"
              className="block max-h-[220px] w-full object-contain object-top"
            />
          </Link>
        )}
        {measure && (
          <div className="font-mono text-[9px] tracking-[0.03em] text-ink2">{measure}</div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <TypeBadge type={unit.unit_type} />
          <span className="font-mono text-[10.5px] text-ink">
            {[unit.pub_date, unit.section_label].filter(Boolean).join(' · ')}
          </span>
          <span className="font-mono text-[10px] uppercase text-ink2">
            {[unit.section, unit.is_wire ? (unit.wire_service ?? 'wire') : 'local']
              .filter(Boolean)
              .join(' · ')}
          </span>
          {!unit.semantic_only &&
            !unit.matched_layers.includes('exact') &&
            (unit.matched_layers.includes('stem') || unit.matched_layers.includes('corrected')) && (
              <span
                className="border border-dashed px-1 font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink2"
                style={{ borderColor: 'var(--color-neutral-400)' }}
                title="Your word does not appear here as typed; a word sharing its stem does (animal: animated)."
              >
                stem match
              </span>
            )}
          {unit.semantic_only && (
            <span
              className="border border-dashed px-1 font-mono text-[8.5px] uppercase tracking-[0.1em] text-accent"
              style={{ borderColor: 'var(--jl-accent)' }}
              title="None of your search words appear in this result. It was found by meaning."
            >
              semantic
            </span>
          )}
          <span className="ml-auto flex items-center gap-1.5">
            {(unit.route || unit.engine) && (
              <span className={`font-mono text-[10px] ${low ? 'text-mark' : 'text-ink2'}`}>
                {[unit.route ? `route ${unit.route}` : null, unit.engine]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
            {unit.confidence !== null && (
              <span
                className={`font-mono text-[11px] ${low ? 'text-mark' : 'text-ink'}`}
                title="How confidently the text of this item was recognised from the page. It says nothing about how well it matches your search."
              >
                {unit.engine === 'pdf-text' ? 'text ' : 'OCR '}
                {(unit.confidence * 100).toFixed(1)}%
              </span>
            )}
          </span>
        </div>

        <h3 className="m-0 mb-[5px] text-[20px] font-semibold leading-[1.2] tracking-[-0.008em]">
          <Link to={`/unit/${unit.id}`} className="text-body no-underline hover:text-accent">
            {unit.headline || <span className="text-ink2">Untitled {unit.unit_type}</span>}
          </Link>
        </h3>

        {(unit.byline || unit.deck) && (
          <div className="mb-[7px] text-[13.5px] text-ink2">
            {unit.byline
              ? unit.unit_type === 'photograph' || /^\s*by\b/i.test(unit.byline)
                ? unit.byline
                : `By ${unit.byline}`
              : unit.deck}
          </div>
        )}

        {unit.snippet ? (
          <div
            className={`mb-[9px] text-[15.5px] leading-[1.5] text-ink ${low ? 'doubt-block' : ''}`}
            // Server-rendered with <mark> around the match. It comes from raw
            // OCR, which is the authoritative record of what the paper printed.
            dangerouslySetInnerHTML={{ __html: unit.snippet }}
          />
        ) : unit.restricted ? (
          <p className="mb-[9px] text-[15px] text-ink2">
            Sign in through your library to read the text and open the page.
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] text-ink2">
          {unit.columns_label && <span>{unit.columns_label}</span>}
          {unit.word_count !== null && <span>{n(unit.word_count)} words</span>}
          <span className="ml-auto flex items-center gap-2.5">
            {openHref && (
              <Link to={openHref} className="text-[10px] tracking-[0.06em]">
                OPEN PAGE
              </Link>
            )}
            {onClip && unit.page_id && unit.region && (
              <button
                type="button"
                onClick={() => onClip(unit)}
                disabled={clipped}
                className="bg-transparent p-0 text-[10px] tracking-[0.06em] text-accent underline underline-offset-2 disabled:no-underline disabled:opacity-60"
              >
                {clipped ? 'CLIPPED' : 'CLIP'}
              </button>
            )}
            <Link to={`/unit/${unit.id}`} className="text-[10px] tracking-[0.06em]">
              CITE
            </Link>
          </span>
        </div>
      </div>
    </article>
  )
}
